import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationListSchema, workQueueSchema, type NotificationList } from "@contracts";
import { queryKeys } from "@lib/queryKeys";
import { apiGet, apiPatch, apiPost } from "@services/newApi/typed";

/** Spec 20261028-132500 AC-8: the count follows the work without a reload — every 60 s, and when the window comes back. */
export const NOTIFICATIONS_REFRESH_MS = 60_000;

export const useNotifications = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.notifications.mine(),
    queryFn: async () => notificationListSchema.parse(await apiGet<unknown>("/notifications")),
    enabled,
    refetchInterval: NOTIFICATIONS_REFRESH_MS,
    refetchOnWindowFocus: true,
  });

/** AC-9: a notice opened is read; the count drops at once, and the list is read again. */
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => apiPatch<unknown>(`/notifications/${Number(id)}/read`),
    onMutate: (id: number) => {
      queryClient.setQueryData<NotificationList>(queryKeys.notifications.mine(), (list) =>
        list
          ? {
              unread: Math.max(0, list.unread - (list.items.some((item) => item.id === id && !item.readAt) ? 1 : 0)),
              items: list.items.map((item) =>
                item.id === id && !item.readAt ? { ...item, readAt: new Date().toISOString() } : item,
              ),
            }
          : list,
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() }),
  });
};

/** AC-9: "Marcar todas como lidas" clears the count. */
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => apiPost<unknown>("/notifications/read-all"),
    onMutate: () => {
      const now = new Date().toISOString();
      queryClient.setQueryData<NotificationList>(queryKeys.notifications.mine(), (list) =>
        list ? { unread: 0, items: list.items.map((item) => ({ ...item, readAt: item.readAt ?? now })) } : list,
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() }),
  });
};

/**
 * AC-11: what waits for the Operação, by hospital; with a hospital active, only that one. AC-12: hospital people never
 * call it — `enabled` is false for them, and the server answers them 403 anyway.
 */
export const useWorkQueue = (hospitalUuid: string | null, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.notifications.workQueue(hospitalUuid),
    queryFn: async () =>
      workQueueSchema.parse(
        await apiGet<unknown>("/work-queue", hospitalUuid ? { params: { hospitalUuid } } : undefined),
      ),
    enabled,
    refetchInterval: NOTIFICATIONS_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
